import type { AuthSession } from './auth'

/**
 * GCP Identity Platform (Firebase Auth) sign-in over the Identity Toolkit REST
 * API — the GCP-side twin of `cognito.ts`. No Firebase SDK: the app talks to the
 * REST endpoints directly with a Web API key, exactly the way `cognito.ts` talks
 * to the Cognito IDP endpoint. The returned ID token is a Firebase JWT whose
 * `workspace_id` + `role` custom claims (set server-side via the Admin API) ride
 * as TOP-LEVEL claims — the backend's `createIdentityPlatformVerifier` reads them
 * from there.
 */

// The Identity Platform WEB API key is a public client identifier (it ships in
// every browser bundle; access control lives in authorized domains + rules), so
// the committed default keeps login working without a build-time env.
const API_KEY = import.meta.env.VITE_HOSTINGPORTAL_IDENTITY_API_KEY ?? 'AIzaSyDRbl0_pfa4zL8uHvZdwnp5YNZYGcBVhMI'
const IDENTITY_ENDPOINT = 'https://identitytoolkit.googleapis.com/v1/accounts'
const TOKEN_ENDPOINT = 'https://securetoken.googleapis.com/v1/token'

interface SignInResponse {
  idToken?: string
  refreshToken?: string
  expiresIn?: string
  localId?: string
  email?: string
  displayName?: string
}

interface IdentityErrorBody {
  error?: {
    code?: number
    message?: string
    errors?: { message?: string; reason?: string }[]
  }
}

export class IdentityAuthError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'IdentityAuthError'
  }
}

/**
 * Kept for interface parity with Cognito's new-password challenge. Identity
 * Platform has no server-driven challenge, so this is never thrown by the live
 * path; it exists so the shared login UI can `import` the same symbol.
 */
export class IdentityNewPasswordRequiredError extends IdentityAuthError {
  readonly email: string
  readonly session: string

  constructor(email: string, session = '') {
    super('Set a permanent password to finish signing in.')
    this.name = 'IdentityNewPasswordRequiredError'
    this.email = email
    this.session = session
  }
}

export async function signInWithIdentityPlatform(
  email: string,
  password: string,
): Promise<AuthSession> {
  const normalizedEmail = email.trim().toLowerCase()
  const body = await identityRequest<SignInResponse>('signInWithPassword', {
    email: normalizedEmail,
    password,
    returnSecureToken: true,
  })
  if (!body.idToken) {
    throw new IdentityAuthError('Identity Platform did not return a usable session.')
  }
  return sessionFromTokens(normalizedEmail, body)
}

/**
 * Invite activation: sign in with the temporary password the admin set, then
 * replace it with the user's permanent one via `accounts:update`. Mirrors
 * Cognito's `activateInvitedUser` so the signup page is provider-agnostic.
 */
export async function activateInvitedUser(args: {
  email: string
  temporaryPassword: string
  newPassword: string
}): Promise<AuthSession> {
  const email = args.email.trim().toLowerCase()
  const signIn = await identityRequest<SignInResponse>('signInWithPassword', {
    email,
    password: args.temporaryPassword,
    returnSecureToken: true,
  })
  if (!signIn.idToken) {
    throw new IdentityAuthError('This invitation could not be activated.')
  }
  const updated = await identityRequest<SignInResponse>('update', {
    idToken: signIn.idToken,
    password: args.newPassword,
    returnSecureToken: true,
  })
  // `accounts:update` returns fresh tokens; fall back to the sign-in tokens if a
  // given deployment withholds them.
  return sessionFromTokens(email, updated.idToken ? updated : signIn)
}

/**
 * Interface-parity shim for Cognito's new-password challenge completion. Identity
 * Platform users set their password through `activateInvitedUser`, so a bare
 * challenge completion just signs in with the new password.
 */
export async function completeNewPasswordChallenge(args: {
  email: string
  session: string
  newPassword: string
}): Promise<AuthSession> {
  return signInWithIdentityPlatform(args.email, args.newPassword)
}

async function identityRequest<T>(
  action: 'signInWithPassword' | 'update' | 'signUp',
  body: Record<string, unknown>,
): Promise<T> {
  if (!API_KEY) {
    throw new IdentityAuthError('Identity Platform is not configured (missing web API key).')
  }
  const res = await fetch(`${IDENTITY_ENDPOINT}:${action}?key=${API_KEY}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as T | IdentityErrorBody
  if (!res.ok) throw new IdentityAuthError(identityErrorMessage(json as IdentityErrorBody))
  return json as T
}

function sessionFromTokens(email: string, body: SignInResponse): AuthSession {
  const claims = decodeJwt(body.idToken ?? '')
  const expiresAt =
    typeof claims.exp === 'number'
      ? claims.exp * 1000
      : Date.now() + Math.max(Number(body.expiresIn ?? '3600'), 60) * 1000
  const name =
    typeof claims.name === 'string'
      ? claims.name
      : body.displayName || undefined
  return {
    email: email.trim().toLowerCase(),
    name,
    idToken: body.idToken,
    // Firebase issues a single ID token used as the bearer; there is no separate
    // access token, so reuse the ID token to satisfy the session shape.
    accessToken: body.idToken,
    refreshToken: body.refreshToken,
    expiresAt,
    // Custom claims are top-level on Firebase tokens (no `custom:` prefix).
    workspaceId: typeof claims.workspace_id === 'string' ? claims.workspace_id : undefined,
    role: typeof claims.role === 'string' ? claims.role : undefined,
    createdAt: new Date().toISOString(),
  }
}

function decodeJwt(token: string): Record<string, unknown> {
  const [, payload] = token.split('.')
  if (!payload) return {}
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    return JSON.parse(atob(padded)) as Record<string, unknown>
  } catch {
    return {}
  }
}

function identityErrorMessage(body: IdentityErrorBody): string {
  const raw = body.error?.message ?? ''
  // Identity Toolkit returns machine codes like `INVALID_LOGIN_CREDENTIALS`.
  const code = raw.split(':')[0]?.trim()
  switch (code) {
    case 'EMAIL_NOT_FOUND':
      return 'No hosting.com account exists for this email yet.'
    case 'INVALID_PASSWORD':
    case 'INVALID_LOGIN_CREDENTIALS':
      return 'Email or password is incorrect.'
    case 'USER_DISABLED':
      return 'This account is disabled. Ask an admin to re-enable it.'
    case 'TOO_MANY_ATTEMPTS_TRY_LATER':
      return 'Too many attempts. Wait a moment and try again.'
    case 'MISSING_PASSWORD':
      return 'Enter your password.'
    default:
      return raw ? humanizeCode(code ?? raw) : 'Could not sign in.'
  }
}

function humanizeCode(code: string): string {
  return code
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
}

/**
 * Exchange the long-lived refresh token for a fresh ID token via the Secure Token
 * endpoint — this is what keeps the login persistent well beyond the 1-hour ID
 * token lifetime (Firebase refresh tokens live until revoked / password change).
 * Returns null on a definitive auth failure (revoked/expired refresh token → the
 * caller logs out); throws on a transient (5xx/network) error so the caller keeps
 * the session and retries later.
 */
export async function refreshSession(session: AuthSession): Promise<AuthSession | null> {
  if (!session.refreshToken || !API_KEY) return null
  const res = await fetch(`${TOKEN_ENDPOINT}?key=${API_KEY}`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: session.refreshToken,
    }).toString(),
  })
  if (!res.ok) {
    if (res.status >= 500) throw new IdentityAuthError('Could not refresh session (server error).')
    return null // 4xx → the refresh token is no longer valid; sign out.
  }
  const json = (await res.json().catch(() => ({}))) as {
    id_token?: string
    refresh_token?: string
    expires_in?: string
  }
  if (!json.id_token) return null
  return sessionFromTokens(session.email, {
    idToken: json.id_token,
    refreshToken: json.refresh_token ?? session.refreshToken,
    expiresIn: json.expires_in,
  })
}

/** Provider bundle consumed by `auth-provider.ts`. */
export const identityPlatformProvider = {
  signIn: signInWithIdentityPlatform,
  activateInvitedUser,
  completeNewPasswordChallenge,
  refresh: refreshSession,
  AuthError: IdentityAuthError,
  NewPasswordRequiredError: IdentityNewPasswordRequiredError,
}
